import type { Adapter, RequestOpts } from './fetch-adapter.js';
import type { LoginResponse, WxLoginResponse, AccountDto, SignedUploadUrl } from './types.js';
import type {
  GrowthDto,
  StatsDto,
  StatsRange,
  TrainingRecordDetailDto,
  RehabRecordDetailDto,
  RehabAppointmentDetailDto,
  AccountPreferencesDto,
  AccountPreferencesPatch,
  PointsSummaryDto,
  PointsTxDto,
  RewardItemDto,
  RewardExchangeDto,
  CouponDto,
} from '@alphastep/shared-types';

export interface ClientOptions {
  baseUrl: string;
  adapter: Adapter;
  getAccessToken?: () => string | undefined;
  onUnauthorized?: () => void;
}

export class Client {
  constructor(private opts: ClientOptions) {}

  private async req<T>(o: Omit<RequestOpts, 'headers'>): Promise<T> {
    const token = this.opts.getAccessToken?.();
    const r = await this.opts.adapter({
      ...o,
      url: `${this.opts.baseUrl}${o.url}`,
      headers: token ? { authorization: `Bearer ${token}` } : {},
    });
    // 登录/刷新接口自身的 401 是「这次凭据不对」，不是「登录态失效」。
    // 无差别触发 onUnauthorized 会让 admin 直接跳转 /login、整页重载，
    // 把调用方即将展示的错误提示一起冲掉——用户点了登录却什么反馈都没有。
    const isAuthEndpoint = /\/auth\/(password-login|login|refresh|wx-login|bind-phone)/.test(o.url);
    if (r.status === 401 && !isAuthEndpoint) this.opts.onUnauthorized?.();
    if (r.status >= 400) {
      const body = (r.body ?? {}) as { code?: string; message?: string };
      throw Object.assign(new Error(body.message ?? 'Request failed'), {
        code: body.code,
        status: r.status,
      });
    }
    return r.body as T;
  }

  // ---------- C 端 ----------
  cWxLogin(code: string) {
    return this.req<WxLoginResponse>({ method: 'POST', url: '/api/c/auth/wx-login', body: { code } });
  }
  cBindPhone(pendingToken: string, phoneCode: string, inviteCode?: string) {
    return this.req<LoginResponse>({
      method: 'POST',
      url: '/api/c/auth/bind-phone',
      body: { pendingToken, phoneCode, inviteCode },
    });
  }
  cRefresh(refreshToken: string) {
    return this.req<{ accessToken: string }>({
      method: 'POST',
      url: '/api/c/auth/refresh',
      body: { refreshToken },
    });
  }
  cLogout(refreshToken: string) {
    return this.req<void>({ method: 'POST', url: '/api/c/auth/logout', body: { refreshToken } });
  }
  cListStudents() {
    return this.req<{ students: unknown[]; currentStudentId: string | null }>({
      method: 'GET',
      url: '/api/c/students',
    });
  }
  cAddStudent(body: {
    name: string;
    birthday: string;
    gender: 'male' | 'female' | 'other';
    homeStoreId: string;
  }) {
    return this.req<unknown>({ method: 'POST', url: '/api/c/students', body });
  }
  cSwitchStudent(studentId: string) {
    return this.req<{ ok: boolean; currentStudentId: string }>({
      method: 'PATCH',
      url: '/api/c/students/current',
      body: { studentId },
    });
  }
  cListStores() {
    return this.req<unknown[]>({ method: 'GET', url: '/api/c/stores' });
  }
  cListSessions(query: { storeId?: string; from: string; to: string; type?: 'training' | 'rehab'; studentId?: string; coachId?: string }) {
    const qs = new URLSearchParams(
      Object.entries(query).filter(([, v]) => v !== undefined) as [string, string][],
    ).toString();
    return this.req<unknown[]>({ method: 'GET', url: `/api/c/course-sessions?${qs}` });
  }
  // #208：C 端教练列表（训练预约「按教练」筛选）
  cListCoaches(storeId?: string) {
    const qs = storeId ? `?storeId=${encodeURIComponent(storeId)}` : '';
    return this.req<unknown[]>({ method: 'GET', url: `/api/c/coaches${qs}` });
  }
  cSessionDetail(id: string) {
    return this.req<unknown>({ method: 'GET', url: `/api/c/course-sessions/${id}` });
  }
  cCreateBooking(body: { courseSessionId: string; studentId: string }) {
    return this.req<unknown>({ method: 'POST', url: '/api/c/bookings', body });
  }
  cListBookings(query?: { status?: string; studentId?: string }) {
    const qs = new URLSearchParams((query ?? {}) as Record<string, string>).toString();
    return this.req<unknown[]>({ method: 'GET', url: `/api/c/bookings${qs ? '?' + qs : ''}` });
  }
  cCancelBooking(id: string, reason?: string) {
    return this.req<{ refunded: boolean }>({
      method: 'DELETE',
      url: `/api/c/bookings/${id}`,
      body: reason ? { reason } : undefined,
    });
  }
  cWaitlistJoin(courseSessionId: string, studentId: string) {
    return this.req<unknown>({ method: 'POST', url: '/api/c/waitlists', body: { courseSessionId, studentId } });
  }
  cWaitlistLeave(courseSessionId: string, studentId: string) {
    return this.req<{ ok: boolean }>({
      method: 'DELETE',
      url: '/api/c/waitlists',
      body: { courseSessionId, studentId },
    });
  }
  cListTherapists(storeId?: string) {
    const qs = storeId ? `?storeId=${encodeURIComponent(storeId)}` : '';
    return this.req<unknown[]>({ method: 'GET', url: `/api/c/therapists${qs}` });
  }
  cCreateRehab(body: {
    studentId: string;
    therapistId: string;
    startAt: string;
    endAt: string;
    symptomNote?: string;
    attachmentUrls?: string[];
  }) {
    return this.req<unknown>({ method: 'POST', url: '/api/c/rehab-appointments', body });
  }
  cListRehabAppointments() {
    return this.req<unknown[]>({ method: 'GET', url: '/api/c/rehab-appointments' });
  }
  cListTrainingRecords(studentId?: string) {
    const qs = studentId ? `?studentId=${encodeURIComponent(studentId)}` : '';
    return this.req<unknown[]>({ method: 'GET', url: `/api/c/records/training${qs}` });
  }
  cListRehabRecords() {
    return this.req<unknown[]>({ method: 'GET', url: '/api/c/records/rehab' });
  }
  cInviteShareContext() {
    return this.req<{ code: string; sharePath: string; posterUrl: string | null }>({
      method: 'GET',
      url: '/api/c/invite/share-context',
    });
  }
  cInviteStats() {
    return this.req<unknown>({ method: 'GET', url: '/api/c/invite/stats' });
  }

  // C 端积分域（G1）
  cGetPoints() {
    return this.req<PointsSummaryDto>({ method: 'GET', url: '/api/c/points' });
  }

  cListPointsTxs(query?: { type?: 'all' | 'gain' | 'use'; cursor?: string; limit?: number }) {
    const qs = new URLSearchParams(
      Object.entries(query ?? {}).filter(([, v]) => v !== undefined) as [string, string][],
    ).toString();
    return this.req<{ items: PointsTxDto[]; nextCursor: string | null }>({
      method: 'GET',
      url: `/api/c/points/txs${qs ? '?' + qs : ''}`,
    });
  }

  cListRewardItems(category?: 'course' | 'goods' | 'welfare') {
    const qs = category ? `?category=${encodeURIComponent(category)}` : '';
    return this.req<{ items: RewardItemDto[] }>({
      method: 'GET',
      url: `/api/c/reward-items${qs}`,
    });
  }

  cRedeemReward(rewardItemId: string) {
    return this.req<{ exchange: RewardExchangeDto }>({
      method: 'POST',
      url: `/api/c/reward-items/${encodeURIComponent(rewardItemId)}/redeem`,
    });
  }

  cListExchanges() {
    return this.req<{ items: RewardExchangeDto[] }>({
      method: 'GET',
      url: '/api/c/exchanges',
    });
  }

  cListCoupons(filter?: 'all' | 'available' | 'used' | 'expired') {
    const qs = filter ? `?filter=${encodeURIComponent(filter)}` : '';
    return this.req<{ items: CouponDto[] }>({
      method: 'GET',
      url: `/api/c/coupons${qs}`,
    });
  }

  // C 端洞察域（G2）
  cGetGrowth(studentId?: string, trajectoryPoints?: number) {
    const qs = new URLSearchParams();
    if (studentId) qs.set('studentId', studentId);
    if (trajectoryPoints) qs.set('trajectoryPoints', String(trajectoryPoints));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.req<GrowthDto>({ method: 'GET', url: `/api/c/growth${suffix}` });
  }

  cGetStats(range: StatsRange, studentId?: string) {
    const qs = new URLSearchParams({ range });
    if (studentId) qs.set('studentId', studentId);
    return this.req<StatsDto>({ method: 'GET', url: `/api/c/stats?${qs.toString()}` });
  }

  // C 端详情域（G3）
  cGetTrainingRecord(id: string) {
    return this.req<TrainingRecordDetailDto>({
      method: 'GET',
      url: `/api/c/records/training/${encodeURIComponent(id)}`,
    });
  }
  cGetRehabRecord(id: string) {
    return this.req<RehabRecordDetailDto>({
      method: 'GET',
      url: `/api/c/records/rehab/${encodeURIComponent(id)}`,
    });
  }
  cGetRehabAppointment(id: string) {
    return this.req<RehabAppointmentDetailDto>({
      method: 'GET',
      url: `/api/c/rehab-appointments/${encodeURIComponent(id)}`,
    });
  }

  // C 端账户域（G4）
  cGetPreferences() {
    return this.req<AccountPreferencesDto>({
      method: 'GET',
      url: '/api/c/account/preferences',
    });
  }

  cUpdatePreferences(patch: AccountPreferencesPatch) {
    return this.req<AccountPreferencesDto>({
      method: 'PATCH',
      url: '/api/c/account/preferences',
      body: patch,
    });
  }

  // C 端：关于我们 / 协议（公开读）
  cGetAboutUs() {
    return this.req<{ contentHtml: string; updatedAt: string | null }>({
      method: 'GET',
      url: '/api/c/system-config/about-us',
    });
  }
  cGetAgreement(type: 'user_agreement' | 'privacy_policy') {
    return this.req<{
      type: 'user_agreement' | 'privacy_policy';
      title: string;
      contentHtml: string;
      version: string;
      updatedAt: string | null;
    }>({
      method: 'GET',
      url: `/api/c/agreements/${encodeURIComponent(type)}`,
    });
  }

  // ---------- B 端 ----------
  bPasswordLogin(phone: string, password: string) {
    return this.req<LoginResponse>({
      method: 'POST',
      url: '/api/b/auth/password-login',
      body: { phone, password },
    });
  }
  bRefresh(refreshToken: string) {
    return this.req<{ accessToken: string }>({
      method: 'POST',
      url: '/api/b/auth/refresh',
      body: { refreshToken },
    });
  }
  bLogout(refreshToken: string) {
    return this.req<void>({ method: 'POST', url: '/api/b/auth/logout', body: { refreshToken } });
  }

  // ---------- admin ----------
  adminPasswordLogin(phone: string, password: string) {
    return this.req<LoginResponse>({
      method: 'POST',
      url: '/api/admin/auth/password-login',
      body: { phone, password },
    });
  }

  // admin: 关于我们
  adminGetAboutUs() {
    return this.req<{ contentHtml: string; updatedAt: string | null; updatedBy: string | null }>({
      method: 'GET',
      url: '/api/admin/system-config/about-us',
    });
  }
  adminPutAboutUs(contentHtml: string) {
    return this.req<{ contentHtml: string; updatedAt: string; updatedBy: string | null }>({
      method: 'PUT',
      url: '/api/admin/system-config/about-us',
      body: { contentHtml },
    });
  }

  // admin: 协议（用户协议 / 隐私政策）
  adminGetAgreement(type: 'user_agreement' | 'privacy_policy') {
    return this.req<{
      type: 'user_agreement' | 'privacy_policy';
      title: string;
      contentHtml: string;
      version: string;
      updatedAt: string | null;
      updatedBy: string | null;
    }>({
      method: 'GET',
      url: `/api/admin/agreements/${encodeURIComponent(type)}`,
    });
  }
  adminPutAgreement(
    type: 'user_agreement' | 'privacy_policy',
    payload: { title?: string; contentHtml: string; version?: string },
  ) {
    return this.req<{
      type: 'user_agreement' | 'privacy_policy';
      title: string;
      contentHtml: string;
      version: string;
      updatedAt: string;
      updatedBy: string | null;
    }>({
      method: 'PUT',
      url: `/api/admin/agreements/${encodeURIComponent(type)}`,
      body: payload,
    });
  }
}

export type { LoginResponse, WxLoginResponse, AccountDto, SignedUploadUrl };
export { fetchAdapter } from './fetch-adapter.js';
export { miniappAdapter } from './miniapp-adapter.js';
